import React, { type MouseEventHandler, useState } from 'react';

import { ReactComponent as ArrowRightIcon } from '@pkg/assets/icons/v2/arrow-right-thick.svg';
import { Badges, GroupCopyLink } from '@pkg/components';
import { useNavigateTop } from '@pkg/hooks';

import { type SocialGroup } from '@app/gen';

import {
    Arrow,
    ButtonWrapper,
    HoverBackground,
    HoverContainer,
    Inside,
    NameContainer,
    StyledDescription,
    StyledFollowerInfo,
    StyledGroupHeaderButton,
    StyledImage,
    StyledLink,
    StyledMembers,
    StyledName,
    Underline,
    ViewButton,
    Wrapper,
} from './styles';

type GroupHeaderHomeProps = Partial<Pick<SocialGroup, 'id' | 'name' | 'members' | 'privacy' | 'description'>> & {
    image?: string;
    isFollowing?: boolean;
    hasMembership?: boolean;
    href?: string;
    isOwner?: boolean;
    followerCount?: number;
    membersCount?: number;
    followerCountWeek?: number;
};

export const GroupHeaderHome = ({
    name,
    description,
    members,
    privacy,
    image,
    isFollowing,
    hasMembership,
    href,
    id,
    isOwner,
    followerCount,
    membersCount,
    followerCountWeek,
}: GroupHeaderHomeProps) => {
    const [hovering, setHovering] = useState(false);
    const navigate = useNavigateTop();
    const showFollowButton = !(isOwner || hasMembership || privacy === 'Private' || isFollowing);
    const finalLink = href || '';

    const handleClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
        e.preventDefault();
        navigate(finalLink);
    };

    return (
        <Wrapper>
            <Inside>
                <StyledLink
                    to={finalLink}
                    onMouseEnter={() => setHovering(true)}
                    onMouseLeave={() => setHovering(false)}
                    onClick={handleClick}
                >
                    <StyledImage variant="home" image={image} />
                    <NameContainer>
                        <HoverContainer>
                            <StyledName name={name} />
                            <Underline $hovering={hovering} />
                        </HoverContainer>

                        <Arrow $hovering={hovering}>
                            <ArrowRightIcon />
                        </Arrow>

                        {href && privacy === 'Public' && (
                            <GroupCopyLink href={`${window.location.origin}${href}`} hovering={hovering} />
                        )}
                    </NameContainer>
                    <StyledDescription description={description} />
                    <StyledFollowerInfo
                        podId={id || ''}
                        membersCount={membersCount}
                        followerCount={followerCount}
                        followerCountWeek={followerCountWeek}
                    />
                    <Badges
                        isFollowing={isFollowing}
                        hasMembership={hasMembership}
                        isOwner={isOwner}
                        privacy={privacy}
                    />
                </StyledLink>

                <StyledMembers groupId={id} members={members} />

                <ButtonWrapper>
                    {!showFollowButton && href && (
                        <ViewButton to={href} onClick={handleClick}>
                            View
                        </ViewButton>
                    )}
                    {showFollowButton && <StyledGroupHeaderButton groupId={id} isFollowing={false} />}
                </ButtonWrapper>
            </Inside>

            <HoverBackground $hovering={hovering} />
        </Wrapper>
    );
};
